import { getCustomFieldViewSections } from "@/components/custom-field/custom-field-view-sections.ts";
import { CustomFieldViewSections } from "@/components/custom-field/CustomFieldViewSections.tsx";
import { linkifyText } from "@/helpers/utils.helper.ts";
import useApplicantCustomFieldsHook from "@/hooks/use-applicant-custom-fields.hook.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { ApplicantDataProtectionListItem } from "@/pages/recruit/candidates/components/ApplicantDataProtectionListItem.tsx";
import { useEntityFormConfig } from "@/queries/use-custom-fields.query.ts";
import { ApiCFEntityTypes, ApiCustomField, ApiFieldTypes } from "@/types/custom-field.types.ts";
import { ApiApplicant, ApiApplicantShort } from "@/types/recruit/applicant.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { Box, HtmlViewer, Stack, Title } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";

const headerFieldTypes = [
  ApiFieldTypes.applicant_photo,
  ApiFieldTypes.applicant_cv,
  ApiFieldTypes.applicant_first_name,
  ApiFieldTypes.applicant_middle_name,
  ApiFieldTypes.applicant_last_name,
];

export interface ApplicantProfileFieldsProps {
  applicant: ApiApplicantShort;
  customFields?: ApiCustomField[];
  applicantFull?: ApiApplicant;
}

export const ApplicantProfileFields = ({
  applicant,
  customFields,
  applicantFull,
}: ApplicantProfileFieldsProps) => {
  const { t } = useTranslation();
  const config = useEntityFormConfig(ApiCFEntityTypes.applicant);
  const { renderApplicantFieldListItem } = useApplicantCustomFieldsHook();
  const { canIEdit } = usePermissions();

  const sections = getCustomFieldViewSections(config.sections, (field) =>
    headerFieldTypes.includes(field.type)
      ? undefined
      : renderApplicantFieldListItem(field, applicant, customFields)
  );
  const dataProtections = canIEdit(ApiPermission.manage_candidates)
    ? (applicantFull?.data_protection ?? [])
    : [];

  if (config.isPending) {
    return <CustomFieldViewSections sections={[]} isPending />;
  }

  return (
    <Stack>
      <CustomFieldViewSections sections={sections} isPending={false} />
      {applicantFull && dataProtections.length > 0 && (
        <>
          <Title
            header={t("data_protection:personal_data_protection")}
            paddingTop={sections.length > 0}
            size={"xs"}
            variant={"secondary"}
          />
          {dataProtections.map((dataProtection) => (
            <ApplicantDataProtectionListItem
              dataProtection={dataProtection}
              applicantFull={applicantFull}
              key={`data_protection_${dataProtection.type}`}
            />
          ))}
        </>
      )}
      {applicant.resume && (
        <>
          <Title
            header={t("core:notes")}
            paddingTop={sections.length > 0 || dataProtections.length > 0}
            size={"xs"}
            variant={"secondary"}
          />
          <Box className={"pt-(--gap-sm)"}>
            <HtmlViewer
              className={"text-secondary whitespace-pre-wrap"}
              content={linkifyText(applicant.resume)}
              maxLines={3}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};
